import {
    LOGIN_USER_START,
    USERNAME_CHANGED,
    PASSWORD_CHANGED,
    LOGIN_RESULT,
    LOGIN_SUCCESS,
    CLEAR_SESSION_MODAL,
    LOGOUT_USER,
    SET_SESSION_FROM_STORAGE,
    EXPIRE_SESSION,
    SET_AUTOSCAN_TIMEOUT,
    SET_SYSTEM_OVERLAY,
    SET_PTZ_CONFIG,
    SET_ALL_CAMERAS,
    SET_PTZ_PRESETS,
    SET_AUTH_SERVERS,
    SET_USER_DATA,
    SET_USER_RIGHTS,
    SET_USER_CAMERAS,
    TOGGLE_EVIEW_LOGIN,
    SET_EVIEW_LOGIN,
    GET_SERVER,
    SET_ALL_EVIEW_CAMERAS,
    SET_SYSTEM_NAME,
    SET_SERVER_SESSIONS,
    SET_NVR_TIMEZONE,
    SET_PW_CHANGE,
    SET_ACCESS_NOTICE
  } from './types';

  import axios from 'axios';
  import history from '../history';
  import { getBackend } from '../backend';
  import { setCloudApiBase, veCall } from '../backend/veCall';
  import { getApiUrl, clearJumpedServerUrl, setJumpedServerUrl, devProxyUrl, buildOrderedMemberUrls } from '../util/getServerInfo';
  import { clearStreamPrefs } from '../util/clearStreamPrefs';
  import { clearSetupStorage } from '../setup/setupCommon';
  import { getViews as getViewsAction, autoSelectDefaultView as autoSelectDefaultViewAction, selectView as selectViewAction, clearLastSelectedView } from './ViewActions';
  import { clearUserSelection, clearLastServer } from '../util/serverPersistence';
  import { persistor } from '../store';
  import { debugLog, debugWarn } from '../util/debug';
  import { runOauthLogin } from '../backend/oauthLogin';
  import { startCloudSessionRefresh, stopCloudSessionRefresh, runOneRefresh } from '../backend/cloudRefresh';
  import { deviceBidToSerial } from '../backend/cloudSession';
  import { selectServerValues } from '../util/serverRights';
  import { getClientVantageHost } from '../util/serverStatus';

  /**
   * Revoke a server session we are about to abandon: drop the local token and tell the backend to
   * invalidate it. Used by the password-expiry path, where the login RPC succeeded (so a real session
   * exists) but we refuse to complete the login. Fire-and-forget — the UI must not wait on it, and a
   * failed revoke still leaves the client with no token. #86bb7phhh
   */
  export const revokeSession = (sSess) => {
    if (!sSess) return Promise.resolve();
    try { localStorage.removeItem('sSess'); } catch (e) { /* ignore */ }
    const wasEviewMode = (() => { try { return localStorage.getItem('eviewMode') === 'true'; } catch (e) { return false; } })();
    return axios({
      method: 'post',
      url: getApiUrl(),
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      data: {
        jsonrpc: 2.0,
        method: wasEviewMode ? 'auth.logoutUserEnterprise' : 'auth.logoutUser',
        id: 205,
        params: [sSess],
      },
      timeout: 6000,
    }).catch(() => { /* best effort — the token is already gone client-side */ });
  };

  export const usernameChanged = text => {
      return {
        type: USERNAME_CHANGED,
        payload: text
      };
    };
    
  export const passwordChanged = text => {
      return {
          type: PASSWORD_CHANGED,
          payload: text
      };
  };
  
  export const toggleEviewLogin = () => {
      return {
          type: TOGGLE_EVIEW_LOGIN
      };
  };

  export const clearSessionModal = () => {
      return {
          type: CLEAR_SESSION_MODAL
      }
  }
  
  export const checkExists = (sSess) => {
      return async ( dispatch ) => {
          return new Promise( async(resolve, reject) => {
              let session = sSess;
              if(!sSess) {
                  session = await localStorage.getItem('sSess');
                  dispatch({ type: SET_SESSION_FROM_STORAGE, payload: session });
              }
              const reqBody = {
                  "jsonrpc": 2.0,
                  "method": "auth.checkExists",
                  "id": 200,
                  "params": [session]
              };
              await axios({
                  method: 'post',
                  url: getApiUrl(),
                  headers: {
                      'Content-Type': 'application/json',
                      'Accept': 'application/json'
                  },
                  data: reqBody,
                  timeout: 3000
              })
              .then( response => {
                  const data = response.data.result[1]
                  if(!data || data === false) {
                      return resolve(false);
                  }
                  resolve(true);
              })
              .catch( (error) => {
                  // REJECT (don't swallow) so the promise always settles. A swallowed
                  // error left validateRehydratedSession's `await` hanging forever, which
                  // with the stay-signed-in resume redirect could strand a user on a
                  // broken /live when the NVR was unreachable. Callers handle it:
                  // validateRehydratedSession's catch -> expireSession -> login;
                  // App.verifySession's catch -> back off + retry (no spurious logout on
                  // a transient blip). This is "server unreachable", distinct from a
                  // definitive invalid session (which resolves false above).
                  debugLog('checkExists error (unreachable):', error && error.message)
                  reject(error);
              });
          })
      }
  }
  
  const isAlive = async() => {
      return new Promise( async(resolve,reject) => {
          const reqBody = {
              "jsonrpc": 2.0,
              "method": "info.isAlive",
              "id": 200
          };
          await axios({
              method: 'post',
              url: getApiUrl(),
              headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
              },
              data: reqBody,
              timeout: 2000
          })
          .then( response => {
              const data = response.data.result[1]
              if(data === false) {
                  resolve(false);
              }
              resolve(true);
          })
          .catch( () => {
              resolve(false);
          }); 
      })
  };
  
export const loginUser = ( sName, sPass, fForce, fLocal, bSerial, fEviewMode = false ) => {
      return async ( dispatch ) => {
          dispatch({ type: LOGIN_USER_START });
          // loginUser is only called for the serving (current) server.
          // Clear any stale jumpedServerUrl so getApiUrl() routes through the proxy
          // to the actual serving server, not a previously-jumped remote server.
          clearJumpedServerUrl();
          localStorage.removeItem('sSess');
          if(sName.trim().length < 1 || sPass.trim().length < 1) {
              dispatch({ type: LOGIN_RESULT, payload: 'loginerror' });
              return setTimeout(() => dispatch({ type: LOGIN_RESULT, payload: '' }), 4000);
          };
          //check server is alive first using method: info.isAlive
          const online = isAlive()
          Promise.all([online])
          .then( async(result) => {
              if(result[0] && result[0] === true) {
              // server is alive - attempt login
                  // Use loginUserEnterprise for eView mode - skips ACL checks, doesn't notify clients
                  // Use loginUser for standard dview mode - enforces ACL and remote rights
                  const reqBody = {
                      "jsonrpc": 2.0,
                      "method": fEviewMode ? "auth.loginUserEnterprise" : "auth.loginUser",
                      "id": 200,
                      "params": [ sName, sPass, fForce ? true : false, fLocal ? true : false ] // [sName, sPass, fForce, fLocal]
                  };
                  debugLog(`[AuthActions] Using ${fEviewMode ? 'loginUserEnterprise' : 'loginUser'} for authentication`);
                  await axios({
                      method: 'post',
                      url: getApiUrl(),
                      headers: {
                          'Content-Type': 'application/json',
                          'Accept': 'application/json'
                      },
                      data: reqBody,
                      timeout: 6000
                  })
                  .then( async response => {
                      const result = response.data.result[1];
                      // possible results: noauth, noremote, exists, maxsession, error, or session key
                      if(result) {

                          if(result.length > 8 & result !== 'maxsession') {
                              // set session key in local storage in case we refresh the page
                              localStorage.setItem('sSess', result);
                              // Store eView mode preference
                              if (fEviewMode) {
                                  localStorage.setItem('eviewMode', 'true');
                              } else {
                                  localStorage.removeItem('eviewMode');
                              }
                              // get all the data we need for user and system, then after we have everything set login success
                              await getPostLogin(dispatch, result, sName, sPass, bSerial, fEviewMode)
                            //   await dispatch({ type: LOGIN_RESULT, payload: '' }); 

                              // push to live page
                            //   history.push('/live'); // this is where we don't get pushed to live for some reason
                          } else {
                                dispatch({ type: LOGIN_RESULT, payload: result });
                          }
                      } else {
                          dispatch({ type: LOGIN_RESULT, payload: "error" });  
                      }
                  })
                  .catch(error => {
                      dispatch({ type: LOGIN_RESULT, payload: "error" }); 
                  })
              } else {
              // if server is not alive show error to use
                  dispatch({ type: LOGIN_RESULT, payload: "error" }); 
              }
          })
          .catch( error => {
          // display error to user
              console.error('Login Error:', error);
          })
      }
  }

  /**
   * The shared cloud-login dispatch tail: everything after the credential/identity
   * step is common to password login and portal Microsoft OAuth login (the adapter
   * has already ingested the /ve/login-shaped payload). Password login runs its own
   * USERNAME_CHANGED + PASSWORD_CHANGED before this; the OAuth path runs only
   * USERNAME_CHANGED (the resolved email) — it never sets PASSWORD_CHANGED.
   * @param {function} dispatch
   * @param {Object} backend - the CloudNvrAdapter (already holding the session)
   * @param {Object} payload - the /ve/login-shaped payload (for .rights + .token)
   */
  const cloudRightsAction = (rights) => {
      const r = rights || { fAccess: 1, fAdmin: 0, fExport: 0, fRemote: 1, fPtz: 0, fPlayback: 1 };
      return {
          type: SET_USER_RIGHTS,
          fAccess: r.fAccess ? 1 : 0,
          fAdmin: r.fAdmin ? 1 : 0,
          fExport: r.fExport ? 1 : 0,
          fRemote: r.fRemote ? 1 : 0,
          fPtz: r.fPtz ? 1 : 0,
          fPlayback: r.fPlayback ? 1 : 0,
      };
  };

  // The cloud user record and the site time zone, from the session the adapter holds: at sign-in
  // and again after an access refresh (a demoted master must lose master-only UI; a removed
  // primary site must not keep its zone; review L12).
  const dispatchCloudIdentity = (dispatch, backend) => {
      // Identity — the header shows state.user.sName. Local login sets this via
      // getUserByName; the cloud user record already rode in on the /ve/login
      // payload (adapter.getUser()). bID is kept for view ownership (Day-4 view CRUD).
      // Other SET_USER_DATA fields are local-NVR-only and stay undefined for cloud.
      const cloudUser = backend.getUser() || {};
      dispatch({
          type: SET_USER_DATA,
          payload: cloudUser.bID,
          sName: cloudUser.sName,
          sDescription: cloudUser.sDescription,
          // bRole drives the view-save role gate in the UI (Global option for admins,
          // Dividia-internal option for bRole=1). The BACKEND is the real gate
          // (modifyOrCreateView §6); this just hides options the user can't use.
          bRole: cloudUser.bRole,
      });
      // Site timezone for the header clock + playback "now" (#86bbm5p92). Cloud has no NVR clock to
      // poll (pollsNvrClock:false); before this it ticked plain browser-local. Seed the primary site
      // zone (JSCustomerProject.sTimezone) so the clock reads the SITE's time (labeled, e.g. CDT) and
      // playback windows default to site-local. '' when no site has one set → the viewer keeps its
      // browser-local fallback (the unset-tz decision). Per-camera zone still resolves per tile.
      dispatch({ type: SET_NVR_TIMEZONE, nvrTimezone: backend.getPrimaryTimezone(), nvrDateFormat: '' });
  };

  const dispatchCloudSessionTail = (dispatch, backend, payload) => {
      // All-devices grid (David's cloud decision): the cloud viewer surfaces EVERY
      // accessible device's cameras at once (the eView `allCameras` model), so a
      // saved view can span devices. getAllCameras() is the cross-device pool.
      //  - SET_ALL_EVIEW_CAMERAS populates state.server.allCameras — the pool the
      //    shared view machinery (selectView) matches view slots against (cloud rides
      //    the eView path; serial = device bID + 9999).
      //  - SET_ALL_CAMERAS seeds the live grid with the same all-devices set: the
      //    default is "all cameras", and the user narrows via the view dropdown.
      const allCams = backend.getAllCameras();
      dispatch({ type: SET_ALL_EVIEW_CAMERAS, payload: allCams });
      dispatch({ type: SET_ALL_CAMERAS, payload: allCams });
      // System label for the header breadcrumb + ⓘ — "Customer — Project" (David
      // #86bb5dx62). The all-devices grid has no single active device, so this is the
      // ACCOUNT/site label (adapter.getSystemLabel). Feeds state.server.sName so every
      // chrome surface (MobileTopBar, DesktopHeader, landscape sidebar, ⓘ) reads it
      // unchanged. Local login fills sName via GET_SERVER instead — modular, no overlap.
      dispatch({ type: SET_SYSTEM_NAME, payload: backend.getSystemLabel() });
      // Identity — the header shows state.user.sName. Local login sets this via
      // getUserByName; the cloud user record already rode in on the /ve/login
      // payload (adapter.getUser()), it just was never dispatched. bID is kept
      // for view ownership (Day-4 view CRUD). Other SET_USER_DATA fields are
      // local-NVR-only and stay undefined for cloud.
      const cloudUser = backend.getUser() || {};
      dispatchCloudIdentity(dispatch, backend);
      // User rights (§3 cloud rights layer): the backend now emits REAL per-user rights
      // derived from JSUser.bRights (auth.js login → `rights` flags), replacing the former
      // temporary grant-all. ADMIN gates Setup entry; EXPORT/PTZ/PLAYBACK/REMOTE gate those
      // actions (the backend is the real gate; the UI mirrors by hiding/disabling). Fall
      // back to a sensible viewer set only if an older (pre-rights) backend omits them, so
      // the product stays usable mid-deploy.
      dispatch(cloudRightsAction(payload.rights));
      // Load the user's saved cloud views into the selector (ViewActions.getViews
      // routes through the cloud adapter's getViewSlotRows AND computes per-device
      // Default + cross-device Smart views), THEN auto-select the default so the grid
      // defaults to a DEVICE VIEW, not the raw all-devices pool
      // (unified-viewer-cloud-views.md §7). The pool (SET_ALL_EVIEW_CAMERAS above)
      // stays as the view-builder picker. Non-blocking: a view failure must not fail
      // login (the all-devices grid seeded above remains as the fallback).
      Promise.resolve(dispatch(getViewsAction(payload.token, cloudUser.bID, false)))
          .then(() => dispatch(autoSelectDefaultViewAction()))
          .catch((e) => debugWarn('[AuthActions] cloud getViews/default-select failed (non-fatal):', e && e.message));
      dispatch({ type: LOGIN_SUCCESS, payload: payload.token });
      history.push('/live');
  };

  /**
   * After the session's access changed (refreshCloudAccess, or the portal's hourly refresh;
   * cloudapi docs/access-refresh-plan.md D, #86bc6dkfa): the new token, the camera pool views match
   * against, rights and the site label, then the views. The view on screen stays unless it shows a
   * device that is gone: then the default view, with a plain notice. A camera that left the view on
   * screen is named in the notice. No devices left ends the session and says why. Unlike the login
   * tail it never resets the grid or navigates.
   * @param {{changed:boolean, removed:Object[], payload:Object}} result - adapter.applySession's
   */
  export const applyRefreshedCloudAccess = (result) => async (dispatch, getState) => {
      if (!result || !result.changed) return false;
      const backend = getBackend();
      const payload = result.payload || {};
      if (!(payload.devices || []).length) {
          dispatch(expireSession(getState().auth && getState().auth.sSess, { cause: 'access_removed' }));
          return false;   // the session ended: nothing for a refused call to retry with (review L9)
      }
      const view = getState().view || {};
      // The cameras as the viewer knew them, before the refreshed pool replaces them: the names for the notice.
      const camerasBefore = (getState().server && getState().server.allCameras) || [];
      const shownView = view.currentView || '';
      const shownSlots = (view.viewSlots && view.viewSlots[view.currentView]) || [];
      const shownSerials = new Set(shownSlots.map((slot) => Number(slot.bSerial)));
      const lost = (result.removed || []).filter((device) => shownSerials.has(deviceBidToSerial(device.bID)));

      dispatch({ type: SET_SESSION_FROM_STORAGE, payload: payload.token });
      dispatch({ type: SET_ALL_EVIEW_CAMERAS, payload: backend.getAllCameras() });
      dispatch({ type: SET_SYSTEM_NAME, payload: backend.getSystemLabel() });
      dispatch(cloudRightsAction(payload.rights));
      dispatchCloudIdentity(dispatch, backend);
      await dispatch(getViewsAction(payload.token, (backend.getUser() || {}).bID, false));
      // A camera of the view on screen is gone only if the refreshed pool no longer holds it (#86bc7maf2).
      // A view can leave the list for other reasons (a device added folds it into a site view, a rename, a
      // view deleted elsewhere); those switch quietly, since nothing was lost.
      const pool = new Set(backend.getAllCameras().map((cam) => `${Number(cam.bSerial)}:${Number(cam.bID)}`));
      const goneSlots = shownSlots.filter((slot) => !pool.has(`${Number(slot.bSerial)}:${Number(slot.bCamera)}`));
      // A gone slot's camera by the name the viewer saw, with its device's name when the view spans devices
      // (two sites can both have a "Gate").
      const cameraName = (slot) => {
          const known = camerasBefore.find((cam) => Number(cam.bSerial) === Number(slot.bSerial) && Number(cam.bID) === Number(slot.bCamera));
          const name = (known && known.sName) || `Camera ${slot.bCamera}`;
          return shownSerials.size > 1 && known && known.sServerName ? `${name} on ${known.sServerName}` : name;
      };

      const current = (getState().view || {}).currentView;
      if (lost.length || !current) {
          await dispatch(autoSelectDefaultViewAction());
          const now = (getState().view || {}).currentView;
          // No view left to select: show the cameras the account still has (the login default), so
          // no tile of a device that is gone stays on the grid.
          if (!now) dispatch({ type: SET_ALL_CAMERAS, payload: backend.getAllCameras() });
          const showing = now ? `showing ${now}` : 'showing the cameras you still have';
          // Say so only when something on screen went (review L6: with no view selected, a device merely
          // added raised this notice): a device of the view, or the view itself because its cameras left.
          if (lost.length) {
              dispatch({
                  type: SET_ACCESS_NOTICE,
                  payload: `${lost.map((device) => device.name).join(', ')} ${lost.length > 1 ? 'are' : 'is'} no longer available `
                      + `to your account; ${showing}.`,
              });
          } else if (shownView && goneSlots.length) {
              dispatch({ type: SET_ACCESS_NOTICE, payload: `The ${shownView} view is no longer available to your account; ${showing}.` });
          }
      } else {
          // The view stays, but its grid is rebuilt from the refreshed pool, so a camera the account
          // lost (a camera grant removed, no device removed) leaves it (review L8). If none of its
          // cameras is left, the grid shows only "Not Found" tiles: say why. If some are left, name the
          // ones that went: a viewer who did not make the change would otherwise watch a camera vanish
          // with no reason and call support (David, 2026-09-28).
          await dispatch(selectViewAction(current));
          if (shownSlots.length && goneSlots.length === shownSlots.length) {
              dispatch({ type: SET_ACCESS_NOTICE, payload: `The cameras in ${current} are no longer available to your account.` });
          } else if (goneSlots.length) {
              const names = [...new Set(goneSlots.map(cameraName))];
              dispatch({ type: SET_ACCESS_NOTICE, payload: `${names.join(', ')} ${names.length > 1 ? 'are' : 'is'} no longer available to your account.` });
          }
      }
      return true;
  };

  /** Returning to the tab refreshes the session's access at most this often (other triggers always run). */
  export const ACCESS_REFRESH_MIN_MS = 60 * 1000;
  let accessRefreshInFlight = null;
  let lastAccessRefreshAt = 0;

  /**
   * Pick up devices granted or removed since sign-in, without signing in again (#86bc6dkfa;
   * cloudapi POST /ve/session/access). Runs on returning to the tab (throttled), when a live stream
   * is refused (force), and once after a device call's 403 (force, via the adapter's
   * setAccessRefresher). Concurrent calls share one request. Resolves true when access changed (so a
   * refused call may retry), false otherwise, including when the session ended. On a 401: the account
   * itself ended (SESSION_ENDED: deleted or disabled) signs out; a portal (Microsoft) session whose
   * 1-hour JWT merely lapsed re-mints through its refresh token instead, signing out only if that is
   * refused too (review L1); a password session's 401 means its 30-day token ended.
   */
  export const refreshCloudAccess = ({ force = false } = {}) => (dispatch, getState) => {
      const backend = getBackend();
      if (!backend || backend.kind !== 'cloud' || typeof backend.refreshAccess !== 'function') return Promise.resolve(false);
      if (!(getState().auth || {}).isLoggedIn) return Promise.resolve(false);
      if (accessRefreshInFlight) return accessRefreshInFlight;
      if (!force && Date.now() - lastAccessRefreshAt < ACCESS_REFRESH_MIN_MS) return Promise.resolve(false);
      lastAccessRefreshAt = Date.now();
      accessRefreshInFlight = (async () => {
          try {
              const result = await backend.refreshAccess();
              // Signed out while the request was out: its reply belongs to no session (review L3).
              if (!(getState().auth || {}).isLoggedIn) return false;
              return await dispatch(applyRefreshedCloudAccess(result));
          } catch (error) {
              if (error && error.response && error.response.status === 401) {
                  const code = error.response.data && error.response.data.code;
                  if (code !== 'SESSION_ENDED' && typeof backend.getRefreshToken === 'function' && backend.getRefreshToken()) {
                      const minted = await runOneRefresh(portalRefreshCallbacks(dispatch, backend));
                      return Boolean(minted && minted.changed);
                  }
                  dispatch(expireSession(getState().auth && getState().auth.sSess));
                  return false;
              }
              debugWarn('[AuthActions] access refresh failed; keeping the session:', error && error.message);
              return false;
          } finally {
              accessRefreshInFlight = null;
          }
      })();
      return accessRefreshInFlight;
  };

  /**
   * Cloud login (product mode CLOUD, env-driven via REACT_APP_PRODUCT_MODE=cloud).
   *
   * A thin dispatcher over the CloudNvrAdapter (getBackend()): POST /ve/login,
   * then read the grid cameras the adapter derived from the payload. ALL cloud
   * logic — JWT/device caching, dragon capability-filtering, dedupe, grid shaping
   * — lives in the adapter (login() + getGridCameras()), so this thunk holds none
   * of it: it just orchestrates the Redux dispatches, mirroring loginUser's tail.
   */
  export const loginCloud = ( username, password ) => {
      return async ( dispatch ) => {
          dispatch({ type: LOGIN_USER_START });
          try {
              const backend = getBackend();
              const payload = await backend.login({ username, password });
              // First-login / admin-reset FORCED password change. cloudapi's /ve/loginV2
              // (auth.js loginUserLite → doPasswordLogin) returns HTTP 200 { changePassword:true, user } and
              // WITHHOLDS the token until the user picks a new password. That legitimate
              // "must change password" state has no token, so it must be caught BEFORE the
              // !token guard below — otherwise it falls through to the generic 'error'
              // ("Server Error / call support") modal, which is what stranded a reset user.
              // Keep the typed temp password in state (PASSWORD_CHANGED) as the oldPassword
              // for /ve/changepassword, stash the bID, and route to the change-pw screen.
              if (payload.changePassword) {
                  dispatch({ type: USERNAME_CHANGED, payload: username });
                  dispatch({ type: PASSWORD_CHANGED, payload: password });
                  dispatch({ type: SET_PW_CHANGE, payload: (payload.user && payload.user.bID) || null });
                  return;
              }
              if (!payload.token) {
                  dispatch({ type: LOGIN_RESULT, payload: 'error' });
                  return;
              }
              // Credentials kept for parity with the local flow (jump/eView reuse
              // them); harmless for cloud.
              dispatch({ type: USERNAME_CHANGED, payload: username });
              dispatch({ type: PASSWORD_CHANGED, payload: password });
              dispatchCloudSessionTail(dispatch, backend, payload);
          } catch (error) {
              // A 4xx from /ve/loginV2 is a REJECTED CREDENTIAL (bad username/password, user not found —
              // cloudapi returns 400 "Could not find user."), NOT a server outage. Surface it as an
              // inline credential error so the user just re-enters — do NOT show the generic "call
              // support" server-error modal (that's for a genuine 5xx/network/timeout failure).
              const status = error && error.response && error.response.status;
              if (status === 400 || status === 401 || status === 403) {
                  dispatch({ type: LOGIN_RESULT, payload: 'invalidcreds' });
              } else {
                  // SECURITY: log only the message — the raw axios error carries error.config.data,
                  // i.e. the POSTed { username, password }, which would land in the devtools console
                  // (and any console-capturing reporter) in production.
                  console.error('[AuthActions] Cloud login error:', (error && error.message) || 'login failed');
                  dispatch({ type: LOGIN_RESULT, payload: 'error' });
              }
          }
      }
  }

  /**
   * Complete a cloud FORCED password change (the 'changepw' flow entered by loginCloud
   * when /ve/login returns { changePassword:true }). POST /ve/changepassword with the
   * temp password the user just logged in with (oldPassword — still in state.auth.password),
   * the chosen newPassword, and the user's bID (state.auth.pwChangeUserId). cloudapi
   * verifies oldPassword, stores the new one, and clears fChangePassword. On success we
   * re-run loginCloud with the NEW password to mint the real session (the change-pw login
   * withheld the token) → the normal cloud tail navigates to /live.
   *
   * Resolves to { ok:true } | { ok:false, message } so the modal can show an inline error
   * and stay open (it never dispatches the generic server-error modal).
   * @param {string} newPassword
   */
  export const changePasswordCloud = ( newPassword ) => {
      return async ( dispatch, getState ) => {
          const { username, password, pwChangeUserId } = getState().auth;
          try {
              const res = await veCall('changepassword', {
                  method: 'post',
                  data: { oldPassword: password, newPassword, id: pwChangeUserId },
              });
              if (!res || !res.data || res.data.success !== true) {
                  const message = (res && res.data && res.data.error) || 'Unable to change password. Please try again.';
                  return { ok: false, message };
              }
              // Password updated + fChangePassword cleared server-side. Re-login with the NEW
              // password to establish the real session (LOGIN_SUCCESS + /live).
              await dispatch(loginCloud(username, newPassword));
              // loginCloud never rejects — on a re-login failure it dispatches its OWN
              // LOGIN_RESULT ('error'/'invalidcreds') and returns nothing. But the password was
              // ALREADY changed, so we must not strand the user on the generic server-error modal
              // (the very thing this feature removes) or a misleading "invalid credentials". If
              // the re-login did not take (a transient blip on the SECOND /ve/loginV2), send them to
              // the login form with a clear "sign in with your new password" notice and clear the
              // now-stale temp password. This runs in the same task as loginCloud's dispatch, so
              // React never paints the intermediate error frame.
              if (!getState().auth.isLoggedIn) {
                  dispatch({ type: PASSWORD_CHANGED, payload: '' });
                  dispatch({ type: LOGIN_RESULT, payload: 'pwchanged' });
              }
              return { ok: true };
          } catch (error) {
              // cloudapi returns 400 { error } for a bad old password / validation failure.
              // Surface it inline; never the call-support server-error modal.
              const message = (error && error.response && error.response.data && error.response.data.error)
                  || 'Unable to change password. Please try again.';
              return { ok: false, message };
          }
      }
  }

  /**
   * Portal Microsoft OAuth login for the HOSTED CloudNVR viewer (card 86bbk70nq).
   * The hosted viewer has no NVR and no serial, so this runs the browser-started
   * portal flow (/ve/portal/ms/*, bound to a first-party cookie) via runOauthLogin
   * in 'session' result mode, ingests the returned cloud session through the SAME
   * adapter as password login, starts the refresh loop (so a Microsoft revocation /
   * admin disable logs the user out), then runs the shared cloud dispatch tail. No
   * password is ever stored. Returns { ok } like loginMicrosoft so LoginView can
   * surface a flow error inline and close its popup.
   */
  // The portal refresh-loop callbacks, shared by login and reload-resume. onToken
  // updates BOTH the adapter token AND the Redux session token (SET_SESSION_FROM_STORAGE)
  // — cloud clip prefetch/download read the Redux token (streamCtx.sSess), so without
  // the Redux update they would keep sending the stale JWT and 401 after ~1 hour.
  // onExpired (a 401 = Microsoft revocation / admin disable) clears the session the
  // way a logout does and routes to login, so a stale LOGIN_SUCCESS can't keep /live
  // reachable in a 401 loop.
  const portalRefreshCallbacks = (dispatch, backend) => ({
      getRefreshToken: () => backend.getRefreshToken(),
      onToken: (t) => { backend.updateToken(t); dispatch({ type: SET_SESSION_FROM_STORAGE, payload: t }); },
      // The refresh returns the whole payload (cloudapi #86bc6dkfa): roll the 1-hour token, and adopt
      // any access change the same way refreshCloudAccess does.
      onSession: (data) => {
          // Signed out meanwhile (the refresh token went with the session): not this session's reply.
          if (!backend.getRefreshToken()) return false;
          const result = backend.applySession(data, { takeToken: true });
          dispatch({ type: SET_SESSION_FROM_STORAGE, payload: data.token });
          return dispatch(applyRefreshedCloudAccess(result));
      },
      onExpired: () => {
          stopCloudSessionRefresh();
          try { backend.logout(); } catch (err) { /* ignore */ }
          try { persistor.purge(); } catch (err) { /* ignore */ }
          dispatch({ type: LOGOUT_USER });
          dispatch({ type: LOGIN_RESULT, payload: '' });
          history.push('/');
      },
  });

  /**
   * Resume the portal refresh loop after a page reload. The CloudNvrAdapter
   * rehydrates the refresh token from localStorage, but nothing restarts the loop,
   * so without this the 1-hour cloud JWT is never re-minted and the portal session
   * silently dies within an hour. Call once on cloud bootstrap. Does an IMMEDIATE
   * refresh first (the persisted JWT may already be expired, or the identity may
   * have been revoked while the tab was closed) — a 401 there boots to login.
   * No-op when there is no portal refresh token (password login / not signed in).
   */
  export const resumePortalSessionRefresh = () => {
      return async ( dispatch ) => {
          const backend = getBackend();
          if (!backend || typeof backend.getRefreshToken !== 'function' || !backend.getRefreshToken()) return;
          const cbs = portalRefreshCallbacks(dispatch, backend);
          await runOneRefresh(cbs);            // immediate: catches an expired JWT / revocation on reload
          startCloudSessionRefresh(cbs);       // then the ongoing loop
      };
  };

  export const loginCloudMicrosoft = ({ navigate, isCancelled } = {}) => {
      return async ( dispatch ) => {
          dispatch({ type: LOGIN_USER_START });
          // Phase 1: the portal broker flow -> a cloud session payload.
          let session;
          try {
              session = await runOauthLogin({
                  provider: 'portal/ms',
                  resultMode: 'session',
                  withCredentials: true,
                  navigate,
                  isCancelled,
              });
          } catch (e) {
              dispatch({ type: LOGIN_RESULT, payload: '' });
              if (e && e.cancelled) return { ok: false, code: 'cancelled' };
              return { ok: false, code: 'flow', message: (e && e.message) || 'Microsoft sign-in failed.' };
          }
          // Phase 2: ingest + wire the refresh loop, then the shared cloud tail.
          try {
              const backend = getBackend();
              backend.establishSession(session);
              backend.setRefreshToken(session.refreshToken);
              startCloudSessionRefresh(portalRefreshCallbacks(dispatch, backend));
              const cloudUser = backend.getUser() || {};
              // The resolved verified email IS the username; no password is stored.
              dispatch({ type: USERNAME_CHANGED, payload: cloudUser.sName || '' });
              dispatchCloudSessionTail(dispatch, backend, session);
              return { ok: true };
          } catch (error) {
              console.error('[AuthActions] Cloud Microsoft login error:', (error && error.message) || 'login failed');
              dispatch({ type: LOGIN_RESULT, payload: 'error' });
              return { ok: false, code: 'error' };
          }
      }
  }

  /**
   * Microsoft OAuth login for the LOCAL NVR (ADR-040 / ADR-047). Runs the cloudapi
   * broker flow (authurl -> browser -> poll -> vouch) via runOauthLogin, then hands
   * the vouch to auth.loginSessionOauth which the NVR verifies LOCALLY and turns
   * into a real session. Mirrors the Java LoginController.startMicrosoftLogin +
   * AuthHelper.loginSessionOauth path.
   *
   * The caller (LoginView) opens a blank popup synchronously inside the click
   * gesture (to dodge popup blockers) and passes `navigate`, which this points at
   * the authUrl once /authurl returns. It also passes the current server's
   * `bSerial` + `sApiToken` (the serial-JWT) read from the pre-login getStartup.
   *
   * fRemote is sent as FALSE to match dview-lite's own password login
   * (loginUser passes fRemote=false), so an OAuth user gets the same ACL gate as a
   * password user on this NVR rather than a spurious 'noremote'.
   *
   * Returns a plain result object so the component can surface the OAuth-flow
   * error inline and close its popup:
   *   { ok:true } | { ok:false, code:'flow'|'noauth'|'noremote'|'maxsession'|'error', message? }
   * Terminal NVR-RPC states (noauth/maxsession/noremote/error) are ALSO dispatched
   * through LOGIN_RESULT so the existing login modals/error text fire unchanged.
   */
  export const loginMicrosoft = ({ bSerial, sApiToken, fEviewMode = false, navigate, isCancelled } = {}) => {
      return async ( dispatch ) => {
          dispatch({ type: LOGIN_USER_START });
          // OAuth logs into the serving NVR; clear any stale jump target and prior session.
          clearJumpedServerUrl();
          localStorage.removeItem('sSess');

          // Phase 1: broker flow -> vouch. isCancelled lets an abandoned popup stop the
          // poll loop promptly instead of freezing the login form for the whole budget.
          let sVouch;
          try {
              sVouch = await runOauthLogin({ provider: 'ms', serial: bSerial, token: sApiToken, navigate, isCancelled });
          } catch (e) {
              // Clear the loading spinner either way. A user cancel (closed the popup) is
              // silent — no error banner; other flow failures surface e.message inline.
              dispatch({ type: LOGIN_RESULT, payload: '' });
              if (e && e.cancelled) {
                  return { ok: false, code: 'cancelled' };
              }
              return { ok: false, code: 'flow', message: (e && e.message) || 'Microsoft sign-in failed.' };
          }

          // Phase 2: exchange the vouch for an NVR session. No session param (there is
          // none yet); params are [vouch, fForce=false, fRemote=false].
          try {
              const reqBody = {
                  "jsonrpc": 2.0,
                  "method": "auth.loginSessionOauth",
                  "id": 200,
                  "params": [ sVouch, false, false ]
              };
              const response = await axios({
                  method: 'post',
                  url: getApiUrl(),
                  headers: {
                      'Content-Type': 'application/json',
                      'Accept': 'application/json'
                  },
                  data: reqBody,
                  timeout: 6000
              });
              const result = response.data.result[1];
              // Success is a { sName, sSess } object; failure is a status string.
              if (result && typeof result === 'object' && result.sSess) {
                  const sSess = result.sSess;
                  const sName = result.sName || '';
                  localStorage.setItem('sSess', sSess);
                  if (fEviewMode) {
                      localStorage.setItem('eviewMode', 'true');
                  } else {
                      localStorage.removeItem('eviewMode');
                  }
                  // OAuth users have no password; pass '' (streams use the serial-JWT / session).
                  await getPostLogin(dispatch, sSess, sName, '', bSerial, fEviewMode);
                  return { ok: true };
              }
              const code = typeof result === 'string' ? result : 'error';
              dispatch({ type: LOGIN_RESULT, payload: code });
              return { ok: false, code };
          } catch (error) {
              debugWarn('[AuthActions] loginMicrosoft exchange error:', error && error.message);
              dispatch({ type: LOGIN_RESULT, payload: 'error' });
              return { ok: false, code: 'error' };
          }
      }
  }

  export const expireSession = ( sSess, reason ) => {
      return ( dispatch ) => {
          // Check if was in eView mode to use correct logout method
          const wasEviewMode = localStorage.getItem('eviewMode') === 'true';
          localStorage.removeItem('sSess');
          localStorage.removeItem('eviewMode'); // Clear eView mode on session expire
          clearStreamPrefs(); // Clear cached stream preferences on session expire
          clearSetupStorage();
          require('../util/liveFrameCache').clearLiveFrames();
          clearJumpedServerUrl(); // Clear jumped server URL on session expire
          clearUserSelection(); // Clear stale server selection from sessionStorage
          // Synchronously drop the persisted root BEFORE the async persistor.purge() — otherwise
          // the old server.serverSessions / auth can rehydrate on re-login and feed a STALE session
          // into the video-stream URLs (metadata/clock still tick via the fresh RPC, but no frames).
          try { localStorage.removeItem('persist:root'); } catch (e) { /* ignore */ }
          persistor.purge(); // Clear persisted Redux state so stale session doesn't rehydrate
          // Also drop the CloudNvrAdapter's persisted session (dl_cloud_session) so an
          // expiry-ended cloud session can't rehydrate account A into account B, AND the
          // cloud last-view key (fixed 'cloud' serial → shared across accounts on one
          // browser). Mirrors logoutUser. No-op-safe for the local adapter (no clearToken;
          // local last-view is keyed per-NVR-serial and intentionally persists — untouched).
          try {
              stopCloudSessionRefresh(); // stop the portal OAuth refresh loop on logout/expiry
              const be = getBackend();
              if (be && be.clearToken) be.clearToken();
              if (be && be.kind === 'cloud') clearLastSelectedView('cloud');
          } catch (e) { /* ignore */ }
          dispatch({ type: EXPIRE_SESSION, reason });   // reason: optional, e.g. { cause: 'access_removed' }
          // Use logoutUserEnterprise for eView - doesn't notify clients
          const reqBody = {
              "jsonrpc": 2.0,
              "method": wasEviewMode ? "auth.logoutUserEnterprise" : "auth.logoutUser",
              "id": 200,
              "params": [ sSess ]
          };
          axios({
              method: 'post',
              url: getApiUrl(),
              headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
              },
              data: reqBody,
              timeout: 6000
          })
          .then( () => {
              history.push('/');
          })
          .catch(error => {
              history.push('/');
          }); 
      }
  }
  
  export const logoutUser = ( sSess, message) => {
      return ( dispatch, getState ) => {
          // On logout, we always reset to the serving server (simple, reliable).
          // User can re-select from Monarch/history if they want the same server again.
          const state = getState();
          const serverInfo = {
              bSerial: state.server.bSerial,
              sName: state.server.sName,
              sIP: state.server.sIP,
              sLocalIP: state.server.sLocalIP,
              bPort: state.server.bPort,
              sVersion: state.server.sVersion,
              sReleaseVersion: state.server.sReleaseVersion,
              fReleaseCandidate: state.server.fReleaseCandidate,
              fEview: state.server.fEview
          };

          // Build logout URL - use the current server we're logged into, not the serving server
          // This ensures logout goes to the correct server in eView/jump scenarios
          let logoutUrl = getApiUrl(); // Default to current (handles jumpedServerUrl)

          // If we have server info and it differs from the serving server, build direct URL
          const jumpedServerUrl = localStorage.getItem('jumpedServerUrl');
          if (!jumpedServerUrl && serverInfo.sLocalIP) {
              // Not jumped, but might be eView - use server's local IP
              const port = serverInfo.bPort || 80;
              const portSuffix = port === 80 ? '' : `:${port}`;
              logoutUrl = `http://${serverInfo.sLocalIP}${portSuffix}/JSON/`;
          }

          // Check if was in eView mode to use correct logout method
          const wasEviewMode = localStorage.getItem('eviewMode') === 'true';
          localStorage.removeItem('sSess');
          localStorage.removeItem('eviewMode'); // Clear eView mode on logout
          clearStreamPrefs(); // Clear cached stream preferences on logout
          clearSetupStorage();
          require('../util/liveFrameCache').clearLiveFrames();
          clearJumpedServerUrl(); // Clear jumped server URL on logout
          clearUserSelection(); // Clear stale server selection from sessionStorage
          try { localStorage.removeItem('persist:root'); } catch (e) { /* ignore */ } // sync — beats the async purge race
          persistor.purge(); // Clear persisted Redux state so stale session doesn't rehydrate
          // Also drop the CloudNvrAdapter's persisted session (dl_cloud_session) so a logged-out
          // cloud user isn't rehydrated on the next load, AND the cloud last-view key (fixed
          // 'cloud' serial → would otherwise bleed account A's last view to account B on the
          // same browser). No-op-safe for the local adapter (no clearToken; local last-view is
          // keyed per-NVR-serial and intentionally persists across logout — leave it untouched).
          try {
              stopCloudSessionRefresh(); // stop the portal OAuth refresh loop on logout/expiry
              const be = getBackend();
              if (be && be.clearToken) be.clearToken();
              if (be && be.kind === 'cloud') clearLastSelectedView('cloud');
          } catch (e) { /* ignore */ }
          dispatch({ type: LOGOUT_USER });

          // Use logoutUserEnterprise for eView - doesn't notify clients
          const reqBody = {
              "jsonrpc": 2.0,
              "method": wasEviewMode ? "auth.logoutUserEnterprise" : "auth.logoutUser",
              "id": 200,
              "params": [ sSess ]
          };
          axios({
              method: 'post',
              url: logoutUrl,
              headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
              },
              data: reqBody,
              timeout: 6000
          })
          .then( () => {
              history.push('/');
              if(message) {
                // this is currently used to set 'inactive' in the login result when our inactivity timer hits 0
                dispatch({ type: LOGIN_RESULT, payload: message });
              }
          })
          .catch(error => {
              history.push('/');
          });
      }
  }
  
  const getPostLogin = async (dispatch, sSess, sName, sPass, bSerial, fEviewMode = false ) => {
      // Get/set all config data needed to display live and playback screens (Standard Viewer)
      // All requests now properly return promises and are awaited
      try {
          debugLog('[AuthActions] Starting post-login data fetch...', fEviewMode ? '(eView mode)' : '(dview mode)');

          // Set eView mode in state explicitly (not toggle - we want to SET the value)
          dispatch({ type: SET_EVIEW_LOGIN, payload: fEviewMode });
          debugLog('[AuthActions] eView mode:', fEviewMode ? 'ENABLED' : 'disabled');

          // Store credentials in Redux state (needed for jump system)
          dispatch({ type: USERNAME_CHANGED, payload: sName });
          dispatch({ type: PASSWORD_CHANGED, payload: sPass });

          // Resolve the SERVING server's serial BEFORE loading user rights/cameras.
          // config.user.getRights/getCameras return one [serial, ...values] array PER
          // server; asked with bServerID=0 they return EVERY server the controller
          // knows about. getUserRights/getUserCameras parse result[1][0] — the FIRST
          // array — so on an eView CONTROLLER where a member serial sorts first, a
          // non-super user (who has an ACL row only on the controller) gets the empty
          // member array and loses ALL rights + cameras (Playback hidden, single-cam
          // nav dead). Superusers are unaffected (every server's array is full). The
          // serving-login path reached here with bSerial=0 because GET_SERVER isn't
          // dispatched until getServerConf runs, so resolve the real serial first and
          // scope the lookups to it — the NVR then returns the single, correct array.
          const serverConf = await getServerConf(dispatch, sSess);
          const servingSerial = (serverConf && serverConf.bSerial) || bSerial || 0;

          // getUserByName must be awaited first as it returns the bUserID needed for getViews
          const userData = await getUserByName(dispatch, sSess, sName, sPass, servingSerial);
          if (userData && userData.pwExpired) {
              // Password expired: getUserByName already dispatched LOGOUT_USER + LOGIN_RESULT('pwexp').
              // Do NOT proceed to LOGIN_SUCCESS / navigate — the login screen shows the pw-change path.
              return;
          }
          const bUserID = userData?.bID || 0;
          debugLog('[AuthActions] User data loaded, bUserID:', bUserID, 'servingSerial:', servingSerial);

          // Fetch the remaining data in parallel - all functions return promises.
          // getServerConf already ran above so rights/cameras get the real serial.
          const [ , , , , authServersData] = await Promise.all([
              systemGetAll(dispatch, sSess),
              ptzGetConfigs(dispatch, sSess),
              getAllCameras(dispatch, sSess),
              ptzGetPresets(dispatch, sSess),
              getAuthServers(dispatch, sSess, sName)
          ]);
          debugLog('[AuthActions] Parallel data fetch complete');

          // In eView mode, fetch cameras from ALL connected servers
          // CRITICAL: This MUST complete BEFORE getViews for Smart Views to work
          let allCamerasCount = 0;
          if (fEviewMode && authServersData && authServersData.length > 0) {
              const localSerial = serverConf?.bSerial || bSerial;
              debugLog('[AuthActions] eView mode: Fetching cameras from all servers...');
              debugLog('[AuthActions] Auth servers available:', authServersData.length);
              try {
                  const allCameras = await fetchAllServerCameras(dispatch, sSess, sName, sPass, authServersData, localSerial);
                  allCamerasCount = allCameras?.length || 0;
                  debugLog('[AuthActions] Successfully fetched', allCamerasCount, 'cameras from all servers');
              } catch (e) {
                  debugWarn('[AuthActions] Failed to fetch all server cameras:', e);
                  // Continue anyway - local cameras are still available
              }
          }

          // Fetch views after other data is loaded (needs bUserID from user data)
          // Views will auto-generate default if none exist
          // In eView mode, pass fEview=true to get views with multi-server slots
          // CRITICAL: Smart Views generation in getViews requires allCameras to be populated
          debugLog('[AuthActions] Loading views - fEviewMode:', fEviewMode, 'allCameras:', allCamerasCount);
          try {
              await dispatch(getViewsAction(sSess, bUserID, fEviewMode));
          } catch (e) {
              debugWarn('[AuthActions] Failed to load views:', e);
          }

          // All data loaded successfully - proceed to live view
          // LOGIN_SUCCESS keeps loading:true, navigation will handle the transition
          // Clear the last server persistence since login succeeded
          clearLastServer();
          dispatch({ type: LOGIN_SUCCESS, payload: sSess });
          history.push('/live');

      } catch (error) {
          console.error('[AuthActions] Post-login error:', error);
          dispatch({ type: LOGIN_RESULT, payload: 'error' });
      }
    }
  
  // All post-login functions now return promises for proper await/Promise.all handling
  const systemGetAll = (dispatch, sSess) => {
      const reqBody = {
          "jsonrpc": 2.0,
          "method": "config.system.getAll",
          "id": 200,
          "params": [ sSess ]
      };
      return axios({
          method: 'post',
          url: getApiUrl(),
          headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
          },
          data: reqBody,
          timeout: 6000
      })
      .then( (response) => {
          let data = response.data.result[1];
          dispatch({ type: SET_AUTOSCAN_TIMEOUT, payload: data?.bAutoScanTimeout || 10 });
          // Capture the NVR burn-in overlay flags here (this login-time getAll is on a fresh post-auth
          // connection that works), so the Setup overlay reads them from the store instead of a fragile
          // on-open getAll. *Supported = key present in getAll (older backends omit newer keys, e.g.
          // fOverlayBackground) → the Setup overlay hides a toggle the NVR can't honor.
          const overlayCfg = (data && typeof data === 'object') ? data : {};
          dispatch({ type: SET_SYSTEM_OVERLAY, payload: {
            fLiveOverlay: !!overlayCfg.fLiveOverlay,
            fOverlayBackground: !!overlayCfg.fOverlayBackground,
            fLiveOverlaySupported: 'fLiveOverlay' in overlayCfg,
            fOverlayBackgroundSupported: 'fOverlayBackground' in overlayCfg
          } });
          return data;
      })
      .catch(error => {
          debugWarn('[AuthActions] systemGetAll error:', error.message);
          dispatch({ type: SET_AUTOSCAN_TIMEOUT, payload: 10 });
          return null;
      });
  }

  const ptzGetConfigs = (dispatch, sSess) => {
      const reqBody = {
          "jsonrpc": 2.0,
          "method": "config.ptz.getAllConfigs",
          "id": 200,
          "params": [ sSess ]
      };
      return axios({
          method: 'post',
          url: getApiUrl(),
          headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
          },
          data: reqBody,
          timeout: 6000
      })
      .then( (response) => {
          let data = response.data.result[1];
          dispatch({ type: SET_PTZ_CONFIG, payload: data });
          return data;
      })
      .catch(error => {
          debugWarn('[AuthActions] ptzGetConfigs error:', error.message);
          return null;
      });
  }

  const getAllCameras = (dispatch, sSess) => {
      const reqBody = {
          "jsonrpc": 2.0,
          "method": "config.camera.getAllCameras",
          "id": 200,
          "params": [ sSess ]
      };
      return axios({
          method: 'post',
          url: getApiUrl(),
          headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
          },
          data: reqBody,
          timeout: 6000
      })
      .then( (response) => {
          let data = response.data.result[1];
          dispatch({ type: SET_ALL_CAMERAS, payload: data });
          return data;
      })
      .catch(error => {
          debugWarn('[AuthActions] getAllCameras error:', error.message);
          return null;
      });
  }

  const ptzGetPresets = (dispatch, sSess) => {
      const reqBody = {
          "jsonrpc": 2.0,
          "method": "config.ptz.getAllPresets",
          "id": 200,
          "params": [ sSess ]
      };
      return axios({
          method: 'post',
          url: getApiUrl(),
          headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
          },
          data: reqBody,
          timeout: 6000
      })
      .then( (response) => {
          let data = response.data.result[1];
          dispatch({ type: SET_PTZ_PRESETS, payload: data });
          return data;
      })
      .catch(error => {
          debugWarn('[AuthActions] ptzGetPresets error:', error.message);
          return null;
      });
  }

  /**
   * Get server configuration - this is the authoritative source of bSerial
   * for the server we're actually connected to.
   * Uses config.meta.getStartup which doesn't require session.
   */
  const getServerConf = (dispatch, sSess) => {
      const reqBody = {
          "jsonrpc": 2.0,
          "method": "config.meta.getStartup",
          "id": 200
          // Note: no session param needed for this method
      };
      return axios({
          method: 'post',
          url: getApiUrl(),
          headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
          },
          data: reqBody,
          timeout: 6000
      })
      .then( (response) => {
          const data = response.data.result[1];
          // Response format: { general: {...}, authServers: [...], usersOnline: [...] }
          const general = data[Object.keys(data)[0]];
          const authServers = data[Object.keys(data)[1]] || [];
          const usersOnline = data[Object.keys(data)[2]] || [];
          // Use fEview property directly (consistent with LoginView fetchConnectedServers)
          // Fallback to rgsFeature check for older server versions that may not have fEview
          const eview = general?.fEview || general?.rgsFeature?.includes('eview') || false;
          const sApiToken = authServers?.[0]?.sApiToken || '';
          setCloudApiBase(general?.sCloudApiUrl || null);

          debugLog('[AuthActions] getServerConf result - bSerial:', general?.bSerial, 'sName:', general?.sName, 'fEview:', eview);

          if (general?.bSerial) {
              // Dispatch GET_SERVER with the actual server info
              dispatch({
                  type: GET_SERVER,
                  bSerial: general.bSerial,
                  sName: general.sName,
                  sVersion: general.sVersion || '',
                  sReleaseVersion: general.sReleaseVersion || '',
                  fReleaseCandidate: !!general.fReleaseCandidate,
                  bNumcams: general.bNumcams || 0,
                  sApiToken: sApiToken,
                  authServers: authServers,
                  usersOnline: usersOnline,
                  fEview: eview
              });
          }
          return general;
      })
      .catch(error => {
          debugWarn('[AuthActions] getServerConf error:', error.message);
          return null;
      });
  }

  const getAuthServers = (dispatch, sSess, sName) => {
      const reqBody = {
          "jsonrpc": 2.0,
          "method": "config.server.getAllAuthServers",
          "id": 200,
          "params": [ sSess, sName ]
      };
      return axios({
          method: 'post',
          url: getApiUrl(),
          headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
          },
          data: reqBody,
          timeout: 6000
      })
      .then( (response) => {
          let result = response.data.result;
          // Handle [success, data] tuple format
          let data = Array.isArray(result) && result.length === 2 ? result[1] : result;
          // Ensure data is an array before sorting
          if (!Array.isArray(data)) {
              debugWarn('[AuthActions] getAuthServers: data is not an array', data);
              data = [];
          }
          let sApiToken = data?.[0]?.sApiToken;
          // organize this data by sName
          let sortedByName = data.sort((a,b) => (a.sName > b.sName) ? 1 : 0);
          dispatch({ type: SET_AUTH_SERVERS, payload: sortedByName, sApiToken: sApiToken });
          return data;
      })
      .catch(error => {
          debugWarn('[AuthActions] getAuthServers error:', error.message);
          return null;
      });
  }

  const checkPwExpired = (expDate, expDays) => {
    // check if password is expired
    if(!expDate || !expDays) {
        return false;
    }
    if(expDays < 1) {
        return false;
    }
    let dPwDate = expDate;
    let dPwExDays = expDays;
    let dPwExpire = new Date(dPwDate * 1000);
    let dPwExpireDate = new Date(dPwExpire);
    let dNow = new Date();
    let dDiff = dPwExpireDate - dNow;
    let dDiffDays = Math.floor(dDiff / (1000 * 60 * 60 * 24));
    let bExpired = false;
    if(dDiffDays < 0) {
        bExpired = true;
    } else if(dDiffDays < dPwExDays) {
        bExpired = true;
    }
    return bExpired;
  }

  const getUserByName = async (dispatch, sSess, sName, sPass, bSerial) => {
      const reqBody = {
          "jsonrpc": 2.0,
          "method": "config.user.getUserByName",
          "id": 200,
          "params": [ sSess, sName ]
      };
      try {
          const response = await axios({
              method: 'post',
              url: getApiUrl(),
              headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
              },
              data: reqBody,
              timeout: 6000
          });

          let data = response.data.result[1];
          if(data) {
              // check for expired password - set show modal if bExpired === true
              let bExpired = await checkPwExpired(data.dPwDate, data.dPwExDays);
              if(bExpired) {
                  // SECURITY (#86bb7phhh): the login RPC already SUCCEEDED and minted a real server
                  // session before we got here, and loginUser wrote it to localStorage. LOGOUT_USER only
                  // clears redux, so without this the rejected login left a LIVE session behind: the
                  // token stayed in localStorage and the backend session stayed valid, so direct RPC
                  // access remained possible and a later checkExists reported it alive. Revoke it
                  // server-side and drop the token before signalling the caller.
                  revokeSession(sSess);
                  dispatch({ type: LOGOUT_USER, payload: 'pwexpired' });
                  dispatch({ type: LOGIN_RESULT, payload: 'pwexp' });
                  // Signal expiry to getPostLogin so it aborts BEFORE LOGIN_SUCCESS/navigate. Previously
                  // this threw and getUserByName's own catch swallowed it to { bID: 0 }, letting
                  // getPostLogin override the logout and navigate an expired-password user into /live.
                  return { bID: 0, pwExpired: true };
              }

              // after we have the user's id we can call to get user rights
              await getUserRights(dispatch, sSess, data.bID, bSerial); // sSess, bID, bSerial
              await getUserCameras(dispatch, sSess, data.bID, bSerial)

              dispatch({
                  type: SET_USER_DATA,
                  payload: data.bID,
                  bID: data.bID,
                  bInactivityTimeout: data.bInactivityTimeout,
                  bType: data.bType,
                  sDescription: data.sDescription,
                  sName: data.sName,
                  sPassword: sPass
              });

              // Return the bUserID for use in getViews
              return { bID: data.bID };
          } else {
              dispatch({ type: LOGIN_RESULT, payload: "error" });
              debugLog('error getting user data');
              return { bID: 0 };
          }
      } catch(error) {
          console.error('Error:', error);
          return { bID: 0 };
      }
  }
  
  const getUserRights = (dispatch, sSess, bID, bSerial) => {
      const reqBody = {   
          "jsonrpc": 2.0,
          "method": "config.user.getRights",
          "id": 200,
          "params": [ sSess, bID, bSerial ]
      };
      // Return the promise so getUserByName (and thus getPostLogin) actually AWAITS
      // rights loading before LOGIN_SUCCESS — otherwise the dispatch races the UI.
      return axios({
          method: 'post',
          url: getApiUrl(),
          headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
          },
          data: reqBody,
          timeout: 6000
      })
      .then( (response) => {
          // Pick THIS server's rights row by serial (see util/serverRights):
          // an eView controller returns one row per server and a member row can
          // sort first and be empty for a non-super user.
          const values = selectServerValues(response.data && response.data.result && response.data.result[1], bSerial);
          let fAccess = 0;
          let fAdmin = 0;
          let fExport = 0;
          let fRemote = 0;
          let fPtz = 0;
          let fPlayback = 0;
  
          if(values.length){
              values.forEach( r => {
                  if(r === 1){
                      fAccess = 1;
                  };
                  if(r === 2){
                      fAdmin = 1;
                  };
                  if(r === 4){
                      fExport = 1;
                  };
                  if(r === 8){
                      fRemote = 1;
                  };
                  if(r === 16){
                      fPtz = 1;
                  };
                  if(r === 32){
                      fPlayback = 1;
                  };
              })
          }
   
          // need to determine if local or remote ip and chack for remote rights as well (if ip is remote)
          if(!fAccess) {
            //   send notification to user that they do not have access to the sytem
              dispatch({ 
                  type: LOGIN_RESULT, 
                  // need to change this to noaccess at some point
                  payload: fRemote === 1 ? 'error' : 'noremote'
              });
              return;
          }
          dispatch({ 
              type: SET_USER_RIGHTS, 
              fAccess,
              fAdmin,
              fExport,
              fRemote,
              fPtz,
              fPlayback     
          })
      })
      .catch(error => {
          console.error('Error:', error);
          dispatch({ 
              type: LOGIN_RESULT, 
              payload: 'error'
          });
      }); 
  }
  
  const getUserCameras = (dispatch, sSess, bID, bSerial) => {
      const reqBody = {
          "jsonrpc": 2.0,
          "method": "config.user.getCameras",
          "id": 200,
          "params": [ sSess, bID, bSerial ]
      };
      // Return the promise so getUserByName (and thus getPostLogin) actually AWAITS
      // camera loading before LOGIN_SUCCESS — otherwise the dispatch races the UI.
      return axios({
          method: 'post',
          url: getApiUrl(),
          headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
          },
          data: reqBody,
          timeout: 6000
      })
      .then( (response) => {
          // selectServerValues picks THIS server's camera row by serial and drops
          // the leading serial element (fixes the old hardcoded `!== 256` filter
          // that leaked the serial as a phantom camera on non-256 NVRs).
          const cameraRightsArr = selectServerValues(response.data && response.data.result && response.data.result[1], bSerial);
          dispatch({ type: SET_USER_CAMERAS, payload: cameraRightsArr })
      })
      .catch(error => {
          console.error('Error:', error);
      });
  }

  /**
   * Login to a different server (pre-login server selection)
   * This is used when user selects a different NVR from the login screen dropdown
   * Similar to jumpSystem but happens at login time instead of after login
   *
   * @param {Object} server - Server object with sIP, sLocalIP, bPort, bSerial, sName
   * @param {string} username - Username
   * @param {string} password - Password
   * @param {boolean} fForce - Force login (kick out existing session)
   */
  export const loginToServer = (server, username, password, fForce = false, fEviewMode = false) => {
    return async (dispatch) => {
      dispatch({ type: LOGIN_USER_START });

      if (!username || username.trim().length < 1 || !password || password.trim().length < 1) {
        dispatch({ type: LOGIN_RESULT, payload: 'loginerror' });
        return setTimeout(() => dispatch({ type: LOGIN_RESULT, payload: '' }), 4000);
      }

      debugLog('[AuthActions] loginToServer:', server.sName, `(${server.bSerial})`, fEviewMode ? '(eView mode)' : '');

      try {
        // Use whichIP logic to find the best connection URL
        const targetUrl = await whichIP(server);
        debugLog('[AuthActions] Selected server URL:', targetUrl);

        // Use dev proxy for cross-origin requests to remote NVRs
        const proxyUrl = devProxyUrl(targetUrl);

        // Step 1: Check if target server is alive
        try {
          const aliveResp = await axios.post(proxyUrl, {
            jsonrpc: 2.0,
            method: 'info.isAlive',
            id: 1
          }, { timeout: 3000 });

          if (!aliveResp.data?.result?.[1]) {
            throw new Error('Server not responding');
          }
        } catch (err) {
          console.error('[AuthActions] Target server not reachable:', err.message);
          dispatch({ type: LOGIN_RESULT, payload: 'error' });
          return;
        }

        // Step 2: Login to target server
        // Use loginUserEnterprise for eView mode - skips ACL checks, doesn't notify clients
        const loginMethod = fEviewMode ? 'auth.loginUserEnterprise' : 'auth.loginUser';
        debugLog(`[AuthActions] Attempting login to target server as: ${username} using ${loginMethod}`);
        let newSession;
        try {
          const loginResp = await axios.post(proxyUrl, {
            jsonrpc: 2.0,
            method: loginMethod,
            id: 2,
            params: [username, password, fForce, true] // fForce, fRemote=true
          }, { timeout: 6000 });

          const result = loginResp.data?.result?.[1];
          if (!result || result.length <= 8 || result === 'maxsession' || result === 'noauth' || result === 'noremote' || result === 'exists') {
            debugLog('[AuthActions] Login result:', result);
            dispatch({ type: LOGIN_RESULT, payload: result || 'error' });
            return;
          }
          newSession = result;
          debugLog('[AuthActions] Login successful, session:', newSession.substring(0, 8) + '...');
        } catch (err) {
          console.error('[AuthActions] Login to target server failed:', err.message);
          dispatch({ type: LOGIN_RESULT, payload: 'error' });
          return;
        }

        // Step 3: Set the jumped server URL so subsequent calls go to this server
        setJumpedServerUrl(targetUrl);
        localStorage.setItem('sSess', newSession);

        // Note: Auto-login only applies to the server serving dview-lite
        // When logging into a different server, we don't set auto-login
        // (user would need to access that server's URL directly for auto-login)
        // Store the USERNAME for convenience. SECURITY (David 2026-08-14): do NOT persist the password —
        // plaintext credentials at rest in localStorage survive logout and are readable by any JS/XSS on
        // the origin (a real risk on shared/field NVR devices). The password is re-entered or supplied by
        // the browser's own password manager.
        localStorage.setItem('username', username);
        localStorage.removeItem('password');

        // Store eView mode preference
        if (fEviewMode) {
          localStorage.setItem('eviewMode', 'true');
        } else {
          localStorage.removeItem('eviewMode');
        }

        // Step 4: Fetch post-login data from the target server
        await getPostLoginFromServer(dispatch, targetUrl, newSession, username, password, server.bSerial, fEviewMode);

        // All data loaded - proceed to live view
        // Clear the last server persistence since login succeeded
        clearLastServer();
        dispatch({ type: LOGIN_SUCCESS, payload: newSession });
        history.push('/live');

      } catch (error) {
        console.error('[AuthActions] loginToServer failed:', error);
        dispatch({ type: LOGIN_RESULT, payload: 'error' });
      }
    };
  };

  /**
   * whichIP - Determine best connection URL for a server
   * Mirrors Java AuthHelper.whichIP() logic
   */
  const whichIP = async (server) => {
    const { sIP, sLocalIP, bPort, bSerial } = server;
    const portSuffix = bPort && bPort !== 80 ? `:${bPort}` : '';

    // Try PUBLIC IP first (5 sec timeout)
    if (sIP) {
      const publicUrl = `http://${sIP}${portSuffix}/JSON/`;
      try {
        debugLog('[whichIP] Trying public:', publicUrl);
        const resp = await axios.post(devProxyUrl(publicUrl), {
          jsonrpc: 2.0,
          method: 'info.getSerial',
          id: 1
        }, { timeout: 5000 });
        const actualSerial = resp.data?.result?.[1];
        if (actualSerial === bSerial) {
          debugLog('[whichIP] Public IP works');
          return publicUrl;
        }
      } catch (e) {
        debugLog('[whichIP] Public failed:', e.message);
      }
    }

    // Try LOCAL IP (1 sec timeout)
    if (sLocalIP && sLocalIP !== '127.0.0.1') {
      const localUrl = `http://${sLocalIP}/JSON/`;
      try {
        debugLog('[whichIP] Trying local:', localUrl);
        const resp = await axios.post(devProxyUrl(localUrl), {
          jsonrpc: 2.0,
          method: 'info.getSerial',
          id: 1
        }, { timeout: 1000 });
        const actualSerial = resp.data?.result?.[1];
        if (actualSerial === bSerial) {
          debugLog('[whichIP] Local IP works');
          return localUrl;
        }
      } catch (e) {
        debugLog('[whichIP] Local failed:', e.message);
      }

      // Try LOCAL IP with port
      if (bPort && bPort !== 80) {
        const localPortUrl = `http://${sLocalIP}:${bPort}/JSON/`;
        try {
          debugLog('[whichIP] Trying local with port:', localPortUrl);
          const resp = await axios.post(devProxyUrl(localPortUrl), {
            jsonrpc: 2.0,
            method: 'info.getSerial',
            id: 1
          }, { timeout: 1000 });
          const actualSerial = resp.data?.result?.[1];
          if (actualSerial === bSerial) {
            debugLog('[whichIP] Local IP with port works');
            return localPortUrl;
          }
        } catch (e) {
          debugLog('[whichIP] Local with port failed:', e.message);
        }
      }
    }

    // Fallback to public IP
    debugLog('[whichIP] Using public IP fallback');
    return `http://${sIP}${portSuffix}/JSON/`;
  };

  /**
   * Get post-login data from a specific server URL
   */
  const getPostLoginFromServer = async (dispatch, serverUrl, sSess, sName, sPass, bSerial, fEviewMode = false) => {
    debugLog('[AuthActions] Fetching post-login data from:', serverUrl, fEviewMode ? '(eView mode)' : '');

    // Use dev proxy for cross-origin requests to remote NVRs
    const proxyUrl = devProxyUrl(serverUrl);

    // Set eView mode in state explicitly (not toggle - we want to SET the value)
    dispatch({ type: SET_EVIEW_LOGIN, payload: fEviewMode });
    debugLog('[AuthActions] eView mode:', fEviewMode ? 'ENABLED' : 'disabled');

    try {
      // Get user data first (need bUserID for views)
      const userResp = await axios.post(proxyUrl, {
        jsonrpc: 2.0,
        method: 'config.user.getUserByName',
        id: 15,
        params: [sSess, sName]
      }, { timeout: 6000 });

      const userData = userResp.data?.result?.[1];
      const bUserID = userData?.bID || 0;

      if (userData) {
        dispatch({
          type: SET_USER_DATA,
          payload: userData.bID,
          bID: userData.bID,
          bInactivityTimeout: userData.bInactivityTimeout,
          bType: userData.bType,
          sDescription: userData.sDescription,
          sName: userData.sName,
          sPassword: sPass
        });
      }

      // Fetch all other data in parallel
      // Per-call resilience (#86bb7phhh): a bare Promise.all rejects the WHOLE batch if any one
      // cross-NVR endpoint is slow/flaky, so none of the SET_* dispatches below run and the user
      // lands on /live with an empty viewer. Each call catches to an empty { data: {} } so the
      // optional-chained guards below skip only the failed leg — matching sibling getPostLogin.
      const softPost = (body) => axios.post(proxyUrl, body, { timeout: 6000 }).catch(() => ({ data: {} }));
      const [systemResp, camerasResp, ptzResp, ptzPresetsResp, serversResp, startupResp, rightsResp, userCamsResp] = await Promise.all([
        softPost({ jsonrpc: 2.0, method: 'config.system.getAll', id: 10, params: [sSess] }),
        softPost({ jsonrpc: 2.0, method: 'config.camera.getAllCameras', id: 11, params: [sSess] }),
        softPost({ jsonrpc: 2.0, method: 'config.ptz.getAllConfigs', id: 12, params: [sSess] }),
        softPost({ jsonrpc: 2.0, method: 'config.ptz.getAllPresets', id: 13, params: [sSess] }),
        softPost({ jsonrpc: 2.0, method: 'config.server.getAllAuthServers', id: 14, params: [sSess, sName] }),
        softPost({ jsonrpc: 2.0, method: 'config.meta.getStartup', id: 18 }),
        softPost({ jsonrpc: 2.0, method: 'config.user.getRights', id: 16, params: [sSess, bUserID, bSerial] }),
        softPost({ jsonrpc: 2.0, method: 'config.user.getCameras', id: 17, params: [sSess, bUserID, bSerial] })
      ]);

      // Dispatch all the data
      if (systemResp.data?.result?.[1]) {
        const sysCfg = systemResp.data.result[1];
        dispatch({ type: SET_AUTOSCAN_TIMEOUT, payload: sysCfg.bAutoScanTimeout || 10 });
        dispatch({ type: SET_SYSTEM_OVERLAY, payload: {
          fLiveOverlay: !!sysCfg.fLiveOverlay,
          fOverlayBackground: !!sysCfg.fOverlayBackground,
          fLiveOverlaySupported: 'fLiveOverlay' in sysCfg,
          fOverlayBackgroundSupported: 'fOverlayBackground' in sysCfg
        } });
      }
      if (camerasResp.data?.result?.[1]) {
        dispatch({ type: SET_ALL_CAMERAS, payload: camerasResp.data.result[1] });
      }
      if (ptzResp.data?.result?.[1]) {
        dispatch({ type: SET_PTZ_CONFIG, payload: ptzResp.data.result[1] });
      }
      if (ptzPresetsResp.data?.result?.[1]) {
        dispatch({ type: SET_PTZ_PRESETS, payload: ptzPresetsResp.data.result[1] });
      }
      if (serversResp.data?.result?.[1]) {
        const servers = serversResp.data.result[1];
        const sortedByName = servers.sort((a, b) => (a.sName > b.sName) ? 1 : 0);
        dispatch({ type: SET_AUTH_SERVERS, payload: sortedByName, sApiToken: servers[0]?.sApiToken });
      }

      // Dispatch GET_SERVER with server info
      if (startupResp.data?.result?.[1]) {
        const data = startupResp.data.result[1];
        const general = data[Object.keys(data)[0]];
        const authServers = data[Object.keys(data)[1]] || [];
        const usersOnline = data[Object.keys(data)[2]] || [];
        // Use fEview property directly (consistent with LoginView fetchConnectedServers)
        const eview = general?.fEview || general?.rgsFeature?.includes('eview') || false;
        const sApiToken = authServers?.[0]?.sApiToken || '';

        dispatch({
          type: GET_SERVER,
          bSerial: general.bSerial,
          sName: general.sName,
          sVersion: general.sVersion || '',
          sReleaseVersion: general.sReleaseVersion || '',
          fReleaseCandidate: !!general.fReleaseCandidate,
          bNumcams: general.bNumcams || 0,
          sApiToken: sApiToken,
          authServers: authServers,
          usersOnline: usersOnline,
          fEview: eview
        });
      }

      // User rights — select THIS server's row by serial (see util/serverRights).
      const rightsValues = selectServerValues(rightsResp.data?.result?.[1], bSerial);
      if (rightsValues.length) {
        let fAccess = 0, fAdmin = 0, fExport = 0, fRemote = 0, fPtz = 0, fPlayback = 0;
        rightsValues.forEach(r => {
          if (r === 1) fAccess = 1;
          if (r === 2) fAdmin = 1;
          if (r === 4) fExport = 1;
          if (r === 8) fRemote = 1;
          if (r === 16) fPtz = 1;
          if (r === 32) fPlayback = 1;
        });
        dispatch({ type: SET_USER_RIGHTS, fAccess, fAdmin, fExport, fRemote, fPtz, fPlayback });
      }

      // User cameras — select THIS server's row by serial (drops leading serial).
      const jumpCams = selectServerValues(userCamsResp.data?.result?.[1], bSerial);
      if (jumpCams.length) {
        dispatch({ type: SET_USER_CAMERAS, payload: jumpCams });
      }

      // In eView mode, fetch cameras from ALL connected servers
      if (fEviewMode && serversResp.data?.result?.[1]) {
        const authServers = serversResp.data.result[1];
        if (authServers && authServers.length > 0) {
          const localSerial = startupResp.data?.result?.[1]?.[Object.keys(startupResp.data.result[1])[0]]?.bSerial || bSerial;
          debugLog('[AuthActions] eView mode: Fetching cameras from all servers...');
          try {
            await fetchAllServerCameras(dispatch, sSess, sName, sPass, authServers, localSerial);
          } catch (e) {
            debugWarn('[AuthActions] Failed to fetch all server cameras:', e);
          }
        }
      }

      // Fetch views from all connected servers
      // IMPORTANT: Must use getViewsAction (from ViewActions) instead of getViewsFromServer
      // In eView mode, getViewsAction iterates over all entries in serverSessions (populated
      // by fetchAllServerCameras above) and aggregates views from every connected NVR.
      // Using getViewsFromServer would only fetch views from the login server, missing
      // views from other NVRs in the enterprise network.
      // In dview mode, getViewsAction falls back to single-server behavior automatically.
      await dispatch(getViewsAction(sSess, bUserID, fEviewMode));

    } catch (error) {
      console.error('[AuthActions] Error fetching post-login data:', error);
    }
  };

  /**
   * Fetch cameras from all connected servers for eView mode
   *
   * IMPORTANT: This mirrors Java's ControllerCameraModel which calls config.server.getCameras
   * on the CONTROLLER ONLY. The controller aggregates camera data from all member servers
   * via the enterprise sync mechanism (ControllerCamera table).
   *
   * This approach does NOT create sessions on member servers, avoiding orphaned sessions.
   * Video streaming uses user/pass authentication, not sessions.
   *
   * @param {function} dispatch - Redux dispatch
   * @param {string} primarySess - Session token for controller server
   * @param {string} username - Username for authentication (used for video URLs)
   * @param {string} password - Password for authentication (used for video URLs)
   * @param {Array} authServers - List of authorized servers (for metadata like IP, name)
   * @param {number} localSerial - Serial of the controller server
   */
  const fetchAllServerCameras = async (dispatch, primarySess, username, password, authServers, localSerial) => {
    debugLog('[AuthActions] fetchAllServerCameras - eView mode (Java-aligned)');
    debugLog('[AuthActions] Using config.server.getCameras on controller only');
    debugLog('[AuthActions] authServers:', authServers?.length, 'localSerial:', localSerial);

    if (!authServers || authServers.length === 0) {
      debugWarn('[AuthActions] No auth servers available');
      return [];
    }

    const allCameras = [];
    const serverSessions = {};

    // Build a lookup map from authServers for server metadata
    const serverMetadata = {};
    authServers.forEach(server => {
      serverMetadata[server.bSerial] = server;
    });

    // Step 1: Call config.server.getCameras on the CONTROLLER
    // This returns aggregated camera data for ALL servers without needing to login to each
    // Backend uses ControllerCamera table for member servers (synced via enterprise mechanism)
    debugLog('[AuthActions] Calling config.server.getCameras on controller...');
    let serverCamerasData = [];
    try {
      const camerasResp = await axios.post(getApiUrl(), {
        jsonrpc: 2.0,
        method: 'config.server.getCameras',
        id: 101,
        params: [primarySess]
      }, { timeout: 10000 });

      serverCamerasData = camerasResp.data?.result?.[1] || [];
      debugLog(`[AuthActions] Got camera data for ${serverCamerasData.length} servers from controller`);
    } catch (err) {
      console.error('[AuthActions] Failed to fetch cameras from controller:', err.message);
      return [];
    }

    // Step 2: Get controller's timezone (used as fallback for all servers)
    // In enterprise deployments, servers typically share the same timezone
    let controllerTimezone = '';
    try {
      const dtimeResp = await axios.post(getApiUrl(), {
        jsonrpc: 2.0,
        method: 'config.dtime.getAll',
        id: 102,
        params: [primarySess]
      }, { timeout: 3000 });
      controllerTimezone = dtimeResp.data?.result?.[1]?.sTimezone || '';
      debugLog('[AuthActions] Controller timezone:', controllerTimezone);
    } catch (err) {
      debugWarn('[AuthActions] Failed to fetch controller timezone, using fallback');
    }

    // Step 3: Resolve each member's stream URL — SYNCHRONOUSLY, no network (#86bb7phhh).
    // This USED to `await Promise.all(authServers.map(whichIP))`: a per-member connectivity probe
    // (public-then-local, up to 3 requests each) for ALL members at once. Gated on the browser's ~6
    // connections-per-host limit, a 132-member controller floods ~7s of /server-check probes here —
    // and getPostLogin AWAITS it, so login can't reach /live until every member is probed. THIS is the
    // real login-latency bottleneck (measured: a 7.0s gap in the auth chain, 132 probes, ALL fired
    // AFTER the login click — the pre-login status-dots sweep was a red herring). whichIP already
    // returned an UNVERIFIED guessed URL on failure, and the video layer (JPEG base + WS overlay) has
    // its own per-stream fallback, so the probe was an optimization, not a correctness requirement.
    // Pick the vantage-preferred IP synchronously (orderCandidateIps: public-first when the client is
    // remote, LAN-first when it's on the member's subnet) — this is the SAME URL whichIP resolves in
    // the common remote→public case, so the current view streams identically, and login is unblocked.
    // (Edge case — a client cross-connected to a member's private subnet where only the LAN IP works:
    // the guess may miss and that member's video falls back; resolve lazily on jump/select if needed.)
    const vantageHost = getClientVantageHost();
    const urlResults = authServers.map((server) => {
      const { bSerial, sName } = server;
      if (bSerial === localSerial) {
        return { bSerial, serverUrl: getApiUrl(), serverUrlAlt: null, sName };   // controller — same-origin URL
      }
      // [preferred, alt] by vantage. serverUrlAlt is the failover the JPEG-error/watchdog path
      // (resolveMemberServerUrl) switches to if the guessed IP turns out unreachable (e.g. NAT hairpin) —
      // so a wrong guess self-heals instead of stranding the member on a "failed" tile. #86bb7phhh
      const urls = buildOrderedMemberUrls(server, vantageHost);
      // Keep ALL alternates. There can be three (public:bPort, LAN:80, LAN:bPort) and dropping the
      // third stranded a member whose only reachable URL was the one discarded. #86bb7phhh
      return { bSerial, serverUrl: urls[0], serverUrlAlt: urls[1] || null, serverUrlAlts: urls.slice(1), sName };
    });

    // Build serverSessions map with URLs (no sessions for member servers - video uses user/pass auth)
    urlResults.forEach(({ bSerial, serverUrl, serverUrlAlt, serverUrlAlts, sName }) => {
      serverSessions[bSerial] = {
        sSess: bSerial === localSerial ? primarySess : null, // Only controller has session
        serverUrl,
        serverUrlAlt,  // first failover candidate (kept for anything still reading the singular field)
        serverUrlAlts, // EVERY failover candidate, in vantage order — what resolveMemberServerUrl walks
        sName,
        sTimezone: controllerTimezone // Use controller timezone for all (common in enterprise)
      };
    });

    // Step 4: Flatten camera data and enrich with server metadata
    serverCamerasData.forEach(serverData => {
      const bSerial = serverData.bSerial;
      const cameras = serverData.rgoCam || [];
      const metadata = serverMetadata[bSerial] || {};
      const sName = metadata.sName || `Server ${bSerial}`;
      const serverUrl = serverSessions[bSerial]?.serverUrl || '';

      cameras.forEach(cam => {
        allCameras.push({
          bID: cam.bID,
          sName: cam.sName,
          fEnable: cam.fEnable || false,
          fPtz: cam.fPtz || false,
          fAudio: cam.fAudio || false,
          fMPE: cam.fMPE || false,
          fFisheye: cam.fFisheye || false,
          // eView-specific fields
          bSerial: bSerial,
          sServerName: sName,
          serverUrl: serverUrl,
          // Display name for eView: "ServerName : CamID - CamName"
          eviewDisplayName: `${sName} : ${String(cam.bID).padStart(2, '0')} ${cam.sName}`,
          compositeKey: `nvr_${bSerial}_cam_${cam.bID}`
        });
      });
    });

    debugLog(`[AuthActions] Total eView cameras: ${allCameras.length} from ${serverCamerasData.length} servers`);
    debugLog('[AuthActions] NO sessions created on member servers (Java-aligned behavior)');

    // Dispatch aggregated cameras
    dispatch({ type: SET_ALL_EVIEW_CAMERAS, payload: allCameras });

    // Dispatch server info for video streaming (uses serverUrl, not session)
    dispatch({ type: SET_SERVER_SESSIONS, payload: serverSessions });

    return allCameras;
  };
